import React, { useEffect, useRef, useState } from "react";
import nacl from "tweetnacl";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ResultBadge from "@/components/ResultBadge";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, Stop, DownloadSimple, WifiSlash, WifiHigh, ClipboardText, ShieldStar, Bell, BellSlash } from "@phosphor-icons/react";
import api from "@/lib/api";

const CACHE_KEYS = "utal_offline_keys";
const CACHE_FEED = "utal_offline_feed";

function b64urlDecode(s) {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const b = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
    const out = new Uint8Array(b.length);
    for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
    return out;
}
function decodeJson(s) {
    return JSON.parse(new TextDecoder().decode(b64urlDecode(s)));
}

function verifySignedToken(token, keys) {
    const parts = token.split(".");
    if (parts.length !== 3) return { valid: false, reason: "malformed" };
    let header, payload;
    try {
        header = decodeJson(parts[0]);
        payload = decodeJson(parts[1]);
    } catch { return { valid: false, reason: "decode_error" }; }
    const sig = b64urlDecode(parts[2]);
    const key = (keys || []).find((k) => k.kid === header.kid);
    if (!key) return { valid: false, reason: "unknown_key", header, payload };
    const pub = b64urlDecode(key.public_key);
    const msg = new TextEncoder().encode(`${parts[0]}.${parts[1]}`);
    const ok = nacl.sign.detached.verify(msg, sig, pub);
    return { valid: ok, reason: ok ? null : "bad_signature", header, payload, key_active: key.active };
}

export default function OfflineScanner() {
    const [online, setOnline] = useState(typeof navigator !== "undefined" && navigator.onLine);
    const [keys, setKeys] = useState(() => JSON.parse(localStorage.getItem(CACHE_KEYS) || "null"));
    const [feed, setFeed] = useState(() => JSON.parse(localStorage.getItem(CACHE_FEED) || "null"));
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [manual, setManual] = useState("");
    const [scanning, setScanning] = useState(false);
    const qrRef = useRef(null);
    const readerId = "utal-offline-qr";

    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    }, []);

    const sync = async () => {
        setBusy(true);
        try {
            const [k, f] = await Promise.all([
                api.get("/keys/public"),
                api.get("/revocations/feed"),
            ]);
            // Verify feed with the key that signed it
            const v = verifySignedToken(f.data.token, k.data.keys);
            if (!v.valid) throw new Error("Feed signature invalid: " + v.reason);
            localStorage.setItem(CACHE_KEYS, JSON.stringify(k.data.keys));
            localStorage.setItem(CACHE_FEED, JSON.stringify({
                signed_at: f.data.generated_at, expires_at: f.data.expires_at,
                payload: v.payload, kid: f.data.kid,
            }));
            setKeys(k.data.keys); setFeed({ signed_at: f.data.generated_at, expires_at: f.data.expires_at, payload: v.payload, kid: f.data.kid });
            toast.success(`Synced ${f.data.count} revocations`);
        } catch (e) {
            toast.error("Sync failed. Try again when online.");
        }
        setBusy(false);
    };

    const verifyLocal = (token) => {
        if (!keys) return { outcome: "red", reasons: ["no_keys_synced"] };
        const v = verifySignedToken(token, keys);
        if (!v.valid) return { outcome: "red", reasons: [v.reason] };
        const p = v.payload;
        const now = Math.floor(Date.now() / 1000);
        const reasons = [];
        let outcome = "green";
        if (p.exp && now > p.exp) { outcome = "amber"; reasons.push("plate_expired"); }
        if (!v.key_active) { outcome = outcome === "green" ? "amber" : outcome; reasons.push("key_rotated"); }
        const revoked = (feed?.payload?.revocations || []).find((r) => r.pid === p.pid || r.plate === p.plate);
        if (revoked) { outcome = "red"; reasons.push("plate_revoked"); }
        return { outcome, reasons, payload: p };
    };

    const start = async () => {
        setResult(null); setScanning(true);
        const scanConfig = {
            fps: 20,
            qrbox: (vfW, vfH) => {
                const minEdge = Math.min(vfW, vfH);
                const size = Math.floor(minEdge * 0.85);
                return { width: size, height: size };
            },
            aspectRatio: 1,
            disableFlip: false,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        };
        try {
            const q = new Html5Qrcode(readerId);
            qrRef.current = q;
            await q.start({ facingMode: "environment" }, scanConfig,
                async (decoded) => { try { await q.stop(); } catch {} setScanning(false); setResult(verifyLocal(decoded)); },
                () => {});
        } catch { toast.error("Camera unavailable"); setScanning(false); }
    };
    const stop = async () => { try { await qrRef.current?.stop(); } catch {} setScanning(false); };
    useEffect(() => () => { qrRef.current?.stop().catch(() => {}); }, []);

    // Subscribe to server revocation push events so an installed PWA syncs immediately.
    useEffect(() => {
        if (!keys) return;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/revocations/stream`;
        let es;
        try {
            es = new EventSource(url);
            es.addEventListener("revocation", () => {
                toast.info("Server posted a new revocation — syncing…");
                sync();
            });
            es.addEventListener("hello", () => { /* pipe established */ });
        } catch { /* ignore */ }
        return () => { try { es?.close(); } catch { /* ignore */ } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keys?.length]);

    // Register service worker so /offline remains installable and boots without the network.
    useEffect(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.register("/sw.js").catch(() => {});
        }
    }, []);

    // Web Push subscription
    const [pushState, setPushState] = useState({ supported: false, subscribed: false, permission: "default" });
    useEffect(() => {
        const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
        if (!supported) { setPushState((s) => ({ ...s, supported: false })); return; }
        (async () => {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            setPushState({ supported: true, subscribed: !!sub, permission: Notification.permission });
        })();
    }, []);

    const urlB64ToUint8 = (b64) => {
        const pad = "=".repeat((4 - (b64.length % 4)) % 4);
        const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
        const out = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
        return out;
    };

    const enablePush = async () => {
        try {
            const perm = await Notification.requestPermission();
            if (perm !== "granted") { toast.error("Notifications blocked"); setPushState((s) => ({ ...s, permission: perm })); return; }
            const { data } = await api.get("/push/vapid-public");
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlB64ToUint8(data.public_key),
            });
            const j = sub.toJSON();
            await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, categories: pushCategories });
            setPushState({ supported: true, subscribed: true, permission: "granted" });
            toast.success("Push notifications enabled");
        } catch (e) {
            toast.error(e?.message || "Failed to enable push");
        }
    };
    const updatePushCategories = async (cats) => {
        setPushCategories(cats);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (!sub) return;
            const j = sub.toJSON();
            await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, categories: cats });
            toast.success("Categories updated");
        } catch { /* ignore */ }
    };
    const [pushCategories, setPushCategories] = useState(["revocation", "fraud", "system"]);
    const [availableCats, setAvailableCats] = useState([
        { k: "revocation", label: "Revocations" },
        { k: "fraud",      label: "Fraud alerts" },
        { k: "system",     label: "System" },
    ]);
    useEffect(() => {
        api.get("/push/categories").then((r) => {
            const cats = (r.data.categories || []).filter((c) => c !== "test");
            const labels = { revocation: "Revocations", fraud: "Fraud alerts", system: "System" };
            setAvailableCats(cats.map((k) => ({ k, label: labels[k] || k.replace(/^\w/, (c) => c.toUpperCase()) })));
        }).catch(() => {});
    }, []);
    const toggleCat = (c) => {
        const next = pushCategories.includes(c) ? pushCategories.filter((x) => x !== c) : [...pushCategories, c];
        if (pushState.subscribed) updatePushCategories(next); else setPushCategories(next);
    };
    const disablePush = async () => {
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                const j = sub.toJSON();
                try { await api.post("/push/unsubscribe", { endpoint: j.endpoint, keys: j.keys }); } catch {}
                await sub.unsubscribe();
            }
            setPushState((s) => ({ ...s, subscribed: false }));
            toast.success("Push notifications disabled");
        } catch { toast.error("Failed to disable push"); }
    };

    return (
        <div className="min-h-screen bg-[#0e0e10] text-white">
            <header className="h-14 border-b border-[#2a2a2a] px-6 flex items-center justify-between">
                <div data-testid="offline-brand">
                    <div className="inline-flex items-center px-2 py-1 rounded-sm bg-white/95">
                        <img src="/utal-mea-logo.png" alt="UTAL MEA LLC" className="h-6 w-auto block select-none" draggable="false" />
                    </div>
                    <div className="text-[10px] tracking-[0.2em] text-white/50 mt-1">OFFLINE FIELD KIT</div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    {online ? (
                        <span className="inline-flex items-center gap-1 text-green-400" data-testid="net-online"><WifiHigh size={14} weight="bold" />ONLINE</span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-amber-400" data-testid="net-offline"><WifiSlash size={14} weight="bold" />OFFLINE</span>
                    )}
                    <Button size="sm" variant="outline" onClick={sync} disabled={busy || !online} className="border-white/30 text-white hover:bg-white/10 rounded-sm" data-testid="sync-btn">
                        <DownloadSimple size={14} weight="bold" className="me-1" />Sync
                    </Button>
                    {pushState.supported && (
                        pushState.subscribed ? (
                            <Button size="sm" variant="outline" onClick={disablePush} className="border-white/30 text-white hover:bg-white/10 rounded-sm" data-testid="push-disable">
                                <BellSlash size={14} weight="bold" className="me-1" />Push On
                            </Button>
                        ) : (
                            <Button size="sm" variant="outline" onClick={enablePush} disabled={!online} className="border-white/30 text-white hover:bg-white/10 rounded-sm" data-testid="push-enable">
                                <Bell size={14} weight="bold" className="me-1" />Enable Push
                            </Button>
                        )
                    )}
                </div>
            </header>

            <div className="max-w-3xl mx-auto p-6 space-y-4">
                {pushState.supported && (
                    <Card className="p-3 rounded-sm bg-[#161618] border-[#2a2a2a] text-white" data-testid="push-categories">
                        <div className="text-[10px] tracking-widest uppercase text-white/50 mb-2 flex items-center gap-2">
                            <Bell size={12} weight="fill" /> Push categories · only what you want pinged
                            {!pushState.subscribed && <span className="ms-auto text-[9px] text-white/40">Pick before Enable Push</span>}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs">
                            {availableCats.map((c) => (
                                <label key={c.k} className={`cursor-pointer px-2.5 py-1 rounded-sm border ${pushCategories.includes(c.k) ? "bg-primary/20 border-primary text-white" : "border-white/20 text-white/60"}`} data-testid={`push-cat-${c.k}`}>
                                    <input type="checkbox" className="me-1 align-middle" checked={pushCategories.includes(c.k)} onChange={() => toggleCat(c.k)} />
                                    {c.label}
                                </label>
                            ))}
                        </div>
                    </Card>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                    <Card className="p-3 rounded-sm bg-[#161618] border-[#2a2a2a] text-white">
                        <div className="text-[10px] tracking-widest uppercase text-white/50">Trusted keys</div>
                        <div className="mono mt-1">{keys?.length ?? 0} cached</div>
                    </Card>
                    <Card className="p-3 rounded-sm bg-[#161618] border-[#2a2a2a] text-white">
                        <div className="text-[10px] tracking-widest uppercase text-white/50">Revocations</div>
                        <div className="mono mt-1" data-testid="rev-count">{feed?.payload?.revocations?.length ?? 0} cached</div>
                        {feed?.signed_at && <div className="text-[10px] text-white/40 mt-1">synced {new Date(feed.signed_at).toLocaleString()}</div>}
                    </Card>
                </div>

                <Card className="p-4 rounded-sm bg-black border-[#2a2a2a]">
                    <div className="viewfinder relative aspect-square max-w-md mx-auto bg-black">
                        <div id={readerId} className="w-full h-full" />
                        {!scanning && <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs tracking-[0.3em] uppercase">Camera idle</div>}
                        <span className="vf-tl" /><span className="vf-tr" /><span className="vf-bl" /><span className="vf-br" />
                    </div>
                    <div className="mt-3 flex justify-center">
                        {!scanning ? (
                            <Button onClick={start} disabled={!keys} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="offline-scan-start">
                                <Camera size={18} weight="fill" className="me-1" />Start Scan
                            </Button>
                        ) : (
                            <Button variant="secondary" onClick={stop} data-testid="offline-scan-stop"><Stop size={18} weight="fill" className="me-1" />Stop</Button>
                        )}
                    </div>
                    {!keys && <div className="text-center text-xs text-amber-400 mt-2">Sync at least once (while online) before scanning offline.</div>}
                </Card>

                <Card className="p-4 rounded-sm bg-[#161618] border-[#2a2a2a] text-white">
                    <div className="text-[10px] tracking-widest uppercase text-white/50 mb-2"><ClipboardText size={12} className="inline me-1" />Manual verify</div>
                    <Textarea rows={3} value={manual} onChange={(e) => setManual(e.target.value)} className="mono text-xs bg-black border-[#2a2a2a] text-white" data-testid="offline-manual-input" />
                    <Button className="mt-2 rounded-sm" onClick={() => setResult(verifyLocal(manual.trim()))} disabled={!manual.trim() || !keys} data-testid="offline-manual-verify">Verify locally</Button>
                </Card>

                {result && (
                    <div className="space-y-3" data-testid="offline-result">
                        <ResultBadge outcome={result.outcome} />
                        {result.reasons?.length > 0 && (
                            <Card className="p-3 rounded-sm bg-[#161618] border-[#2a2a2a] text-white">
                                <div className="text-[10px] tracking-widest uppercase text-white/50 mb-1">Signals</div>
                                <ul className="text-xs mono">{result.reasons.map((r) => <li key={r}>· {r}</li>)}</ul>
                            </Card>
                        )}
                        {result.payload && (
                            <Card className="p-3 rounded-sm bg-[#161618] border-[#2a2a2a] text-white">
                                <div className="text-[10px] tracking-widest uppercase text-white/50 mb-1">Payload</div>
                                <div className="grid grid-cols-2 gap-2 text-xs mono">
                                    <div>Plate: <b>{result.payload.plate}</b></div>
                                    <div>VIN: {result.payload.vin}</div>
                                    <div>{result.payload.make} {result.payload.model}</div>
                                    <div>Year: {result.payload.year}</div>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
